import { FormBuilder, FormGroup } from '@angular/forms';
import { updateDependentControl } from '@shared/helpers/utils.helper';
import { CoopValidators } from '@core/helpers/validators.helper';
import {
    MyAllAmminUnicoEntity,
    MyAllElemCollegioSindacaleEntity,
    MyAllElemConsiglioAmministrazioneEntity,
    MyAllElemLiquidatoriEntity,
    MyAllElemRevisoriControlloContabileEntity,
    MySpdElemConsiglioGestioneEntity,
    MySpdElemConsiglioSorveglianzaEntity,
    MySpmElemComitatoGestioneEntity,
    MyVerbale,
} from '../../../../models/interfaces/revisione.interface';
import { getFormArray, rebuildFormArray } from '@shared/helpers/form.helper';
import { formPaths } from '@shared/constants/form-paths.const';

/**
 * Path (relativi al form del norme-service) di tutti i FormArray gestiti dalle norme amm-controllo.
 * Usati sia dai builder sia da {@link clearNormeFormArrays}; unica fonte di verità per evitare duplicazione.
 */
export const NORME_ARRAY_PATHS = {
    revisori: `${formPaths.ammControllo}.my_AllRevisoriControlloContabile.my_AllElemRevisoriControlloContabile`,
    consiglioAmministrazione: `${formPaths.ammControllo}.my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione`,
    amministratoreUnico: `${formPaths.ammControllo}.my_AllAmminUnico`,
    liquidatori: `${formPaths.ammControllo}.my_AllLiquidatori.my_AllElemLiquidatori`,
    collegioSindacale: `${formPaths.ammControllo}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`,
    consiglioSorveglianza: `${formPaths.ammControllo}.my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza`,
    consiglioGestione: `${formPaths.ammControllo}.my_NormeSpaDualistico.my_SpdConsiglioGestione.my_SpdElemConsiglioGestione`,
    comitatoGestione: `${formPaths.ammControllo}.my_NormeSpaMonistico.my_SpmComitatoGestione.my_SpmElemComitatoGestione`,
} as const;

/**
 * Svuota tutti i FormArray norme presenti nel form dato. Difensivo: ogni norme-service dichiara solo
 * un sottoinsieme di questi path, quindi ignora quelli assenti. Va chiamato quando la norma del verbale
 * corrente NON è quella del service, per garantire l'isolamento totale tra verbali (nessun residuo).
 */
export const clearNormeFormArrays = (form: FormGroup): void => {
    Object.values(NORME_ARRAY_PATHS).forEach((path) => {
        const formArray = getFormArray(form, path);
        if (formArray && formArray.length > 0) formArray.clear();
    });
};

export const buildRevisoreFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const consAmm = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllRevisoriControlloContabile.my_AllElemRevisoriControlloContabile;
    rebuildFormArray(fb, consAmm, form, NORME_ARRAY_PATHS.revisori, buildRevisoreGroup);
};

export const buildRevisoreGroup = (fb: FormBuilder, item?: MyAllElemRevisoriControlloContabileEntity): FormGroup => {
    return fb.group({
        my_AllRevisoriControlloContabileCarica: item?.my_AllRevisoriControlloContabileCarica,
        my_AllRevisoriControlloContabileCognome: item?.my_AllRevisoriControlloContabileCognome,
        my_AllRevisoriControlloContabileNome: item?.my_AllRevisoriControlloContabileNome,
        my_AllRevisoriControlloContabileCodFisc: item?.my_AllRevisoriControlloContabileCodFisc,
        my_AllRevisoriControlloContabileDataElezione: item?.my_AllRevisoriControlloContabileDataElezione,
        my_AllRevisoriControlloContabileCodFiscOrig: item?.my_AllRevisoriControlloContabileCodFiscOrig,
    });
};

export const buildConsiglioAmministrazioneFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const consAmm = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione;
    rebuildFormArray(fb, consAmm, form, NORME_ARRAY_PATHS.consiglioAmministrazione, buildConsiglioamministrazioneGroup);
};

export const buildConsiglioamministrazioneGroup = (fb: FormBuilder, item?: MyAllElemConsiglioAmministrazioneEntity): FormGroup => {
    const group = fb.group({
        my_AllConsAmmCarica: item?.my_AllConsAmmCarica,
        my_AllConsAmmCognome: item?.my_AllConsAmmCognome,
        my_AllConsAmmNome: item?.my_AllConsAmmNome,
        my_AllConsAmmDataElezione: item?.my_AllConsAmmDataElezione,
        my_AllConsAmmCkSocio: item?.my_AllConsAmmCkSocio?.toString(),
        my_AllConsAmmVia: item?.my_AllConsAmmVia,
        my_AllConsAmmComuneCH: item?.my_AllConsAmmComuneCH ?? undefined,
        my_AllConsAmmProvinciaCH: item?.my_AllConsAmmProvinciaCH ?? undefined,
        my_AllConsAmmCap: item?.my_AllConsAmmCap,
        my_AllConsAmmCodFisc: item?.my_AllConsAmmCodFisc,
        my_AllConsAmmCkRevisore: item?.my_AllConsAmmCkRevisore,
        my_AllConsAmmCodFiscOrig: item?.my_AllConsAmmCodFiscOrig,
        my_AllConsAmmIdOrganoDiCons: item?.my_AllConsAmmIdOrganoDiCons,
        my_AllConsAmmIdOrgano: item?.my_AllConsAmmIdOrgano,
        my_AllConsAmmProvincia: item?.my_AllConsAmmProvincia,
        my_AllConsAmmComune: item?.my_AllConsAmmComune,
        my_AllConsAmmIdComune: item?.my_AllConsAmmIdComune,
        my_AllConsAmmIdProvincia: item?.my_AllConsAmmIdProvincia,
        my_AllConsAmmIdIndirizzo: item?.my_AllConsAmmIdIndirizzo,
        my_AllConsAmmDataElezioneOrig: item?.my_AllConsAmmDataElezioneOrig,
        my_AllConsAmmCaricaOrig: item?.my_AllConsAmmCaricaOrig,
        my_group16: item?.my_group16,
        my_group18: item?.my_group18,
        my_group21: item?.my_group21,
        my_group25: item?.my_group25,
        my_AllConsAmmCaricaDesc: item?.my_AllConsAmmCaricaDesc,
        my_AllConsAmmEmail: item?.my_AllConsAmmEmail,
    });
    updateDependentControl(group, 'my_AllConsAmmComuneCH', 'my_AllConsAmmComune');
    updateDependentControl(group, 'my_AllConsAmmProvinciaCH', 'my_AllConsAmmProvincia');
    return group;
};

export const buildAmministratoreUnicoFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup, withValidators = true) => {
    const amminUnico = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllAmminUnico;
    rebuildFormArray(fb, amminUnico, form, NORME_ARRAY_PATHS.amministratoreUnico, (fbInner, item) => buildAmministratoreUnicoGroup(fbInner, item, withValidators));
};

export const buildAmministratoreUnicoGroup = (fb: FormBuilder, item?: MyAllAmminUnicoEntity, withValidators = true): FormGroup => {
    const group = fb.group({
        my_AllAmmUnCarica: item?.my_AllAmmUnCarica ?? '072',
        my_AllAmmUnCognome: item?.my_AllAmmUnCognome,
        my_AllAmmUnNome: item?.my_AllAmmUnNome,
        my_AllAmmUnCodFisc: item?.my_AllAmmUnCodFisc,
        my_AllAmmUnEmail: item?.my_AllAmmUnEmail,
        my_AllAmmUnVia: item?.my_AllAmmUnVia,
        my_AllAmmUnProvinciaCH: withValidators ? [item?.my_AllAmmUnProvinciaCH, CoopValidators.required] : [item?.my_AllAmmUnProvinciaCH],
        my_AllAmmUnComuneCH: withValidators ? [item?.my_AllAmmUnComuneCH, CoopValidators.required] : [item?.my_AllAmmUnComuneCH],
        my_AllAmmUnCap: item?.my_AllAmmUnCap,
        my_AllAmmUnDataElezione: [item?.my_AllAmmUnDataElezione],
        my_AllAmmUnCkSocio: item?.my_AllAmmUnCkSocio,
        my_AllAmmUnCkRevisore: item?.my_AllAmmUnCkRevisore ?? '1',
        my_AllAmmUnCodFiscOrig: item?.my_AllAmmUnCodFiscOrig ?? '1',
        my_AllAmmUnCaricaDesc: item?.my_AllAmmUnCaricaDesc ?? 'AMMINISTRATORE UNICO',
        my_AllAmmUnProvincia: item?.my_AllAmmUnProvincia,
        my_AllAmmUnComune: item?.my_AllAmmUnComune,
        my_AllAmmUnIdComune: item?.my_AllAmmUnIdComune,
        my_AllAmmUnIdProvincia: item?.my_AllAmmUnIdProvincia,
        my_AllAmmUnIdIndirizzo: item?.my_AllAmmUnIdIndirizzo,
        my_SRLAmmUnicoSocio: item?.my_SRLAmmUnicoSocio ?? '1',
        my_AllAmmUnDataElezioneOrig: item?.my_AllAmmUnDataElezioneOrig,
        my_AllAmmUnCaricaOrig: item?.my_AllAmmUnCaricaOrig,
        my_group19: item?.my_group19,
        my_group22: item?.my_group22,
    });
    updateDependentControl(group, 'my_AllAmmUnComuneCH', 'my_AllAmmUnComune');
    updateDependentControl(group, 'my_AllAmmUnProvinciaCH', 'my_AllAmmUnProvincia');
    return group;
};

export const buildLiquidatoriFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const liquidatori = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllLiquidatori.my_AllElemLiquidatori;
    rebuildFormArray(fb, liquidatori, form, NORME_ARRAY_PATHS.liquidatori, buildLiquidatoriGroup);
};

export const buildLiquidatoriGroup = (fb: FormBuilder, item?: MyAllElemLiquidatoriEntity): FormGroup => {
    const group = fb.group({
        my_AllLiquidatoriCarica: item?.my_AllLiquidatoriCarica ?? '075',
        my_AllLiquidatoriCognome: item?.my_AllLiquidatoriCognome,
        my_AllLiquidatoriNome: item?.my_AllLiquidatoriNome,
        my_AllLiquidatoriCodFisc: item?.my_AllLiquidatoriCodFisc,
        my_AllLiquidatoriEmail: item?.my_AllLiquidatoriEmail,
        my_AllLiquidatoriVia: item?.my_AllLiquidatoriVia,
        my_AllLiquidatoriProvinciaCH: item?.my_AllLiquidatoriProvinciaCH,
        my_AllLiquidatoriComuneCH: item?.my_AllLiquidatoriComuneCH,
        my_AllLiquidatoriCap: item?.my_AllLiquidatoriCap,
        my_AllLiquidatoriDataElezione: item?.my_AllLiquidatoriDataElezione,
        my_AllLiquidatoriCkSocio: item?.my_AllLiquidatoriCkSocio ?? '1',
        my_AllLiquidatoriCaricaDesc: item?.my_AllLiquidatoriCaricaDesc ?? 'LIQUIDATORE',
        my_AllLiquidatoriCodFiscOrig: item?.my_AllLiquidatoriCodFiscOrig,
        my_AllLiquidatoriProvincia: item?.my_AllLiquidatoriProvincia,
        my_AllLiquidatoriComune: item?.my_AllLiquidatoriComune,
        my_AllLiquidatoriIdComune: item?.my_AllLiquidatoriIdComune,
        my_AllLiquidatoriIdProvincia: item?.my_AllLiquidatoriIdProvincia,
        my_AllLiquidatoriIdIndirizzo: item?.my_AllLiquidatoriIdIndirizzo,
        my_group28: item?.my_group28,
        my_AllLiquidatoriCaricaOrig: item?.my_AllLiquidatoriCaricaOrig,
        my_AllLiquidatoriDataElezioneOrig: item?.my_AllLiquidatoriDataElezioneOrig,
    });
    updateDependentControl(group, 'my_AllLiquidatoriComuneCH', 'my_AllLiquidatoriComune');
    updateDependentControl(group, 'my_AllLiquidatoriProvinciaCH', 'my_AllLiquidatoriProvincia');
    return group;
};

export const buildOrganoControlloFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const elemColl = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllCollegioSindacale.my_AllElemCollegioSindacale;
    rebuildFormArray(fb, elemColl, form, NORME_ARRAY_PATHS.collegioSindacale, buildOrganoControlloGroup);
};

export const buildOrganoControlloGroup = (fb: FormBuilder, item?: MyAllElemCollegioSindacaleEntity): FormGroup => {
    const group = fb.group({
        my_AllCollSindCarica: item?.my_AllCollSindCarica,
        my_AllCollSindCognome: item?.my_AllCollSindCognome,
        my_AllCollSindNome: item?.my_AllCollSindNome,
        my_AllCollSindDataElezione: item?.my_AllCollSindDataElezione,
        my_AllCollSindVia: item?.my_AllCollSindVia,
        my_AllCollSindProvinciaCH: item?.my_AllCollSindProvinciaCH,
        my_AllCollSindComuneCH: item?.my_AllCollSindComuneCH,
        my_AllCollSindCap: item?.my_AllCollSindCap,
        my_AllCollSindCodFisc: item?.my_AllCollSindCodFisc,
        my_AllCollSindCkRevisore: item?.my_AllCollSindCkRevisore ?? '1',
        my_AllCollSindCkSocio: item?.my_AllCollSindCkSocio ?? '1',
        my_AllCollSindCodFiscOrig: item?.my_AllCollSindCodFiscOrig,
        my_AllCollSindIdOrganoDiCons: item?.my_AllCollSindIdOrganoDiCons,
        my_AllCollSindIdOrgano: item?.my_AllCollSindIdOrgano,
        my_AllCollSindProvincia: item?.my_AllCollSindProvincia,
        my_AllCollSindComune: item?.my_AllCollSindComune,
        my_AllCollSindIdComune: item?.my_AllCollSindIdComune,
        my_AllCollSindIdProvincia: item?.my_AllCollSindIdProvincia,
        my_AllCollSindIdIndirizzo: item?.my_AllCollSindIdIndirizzo,
        my_AllCollSindDataElezioneOrig: item?.my_AllCollSindDataElezioneOrig,
        my_AllCollSindCaricaOrig: item?.my_AllCollSindCaricaOrig,
        my_group20: item?.my_group20,
        my_group23: item?.my_group23,
        my_AllCollSindCaricaDesc: item?.my_AllCollSindCaricaDesc,
        my_AllCollSindEmail: item?.my_AllCollSindEmail,
    });
    updateDependentControl(group, 'my_AllCollSindProvinciaCH', 'my_AllCollSindProvincia');
    updateDependentControl(group, 'my_AllCollSindComuneCH', 'my_AllCollSindComune');
    return group;
};

export const buildElemConsSorveglianzaGroup = (fb: FormBuilder, item?: MySpdElemConsiglioSorveglianzaEntity): FormGroup => {
    const group = fb.group({
        my_SpdConsSorvCarica: item?.my_SpdConsSorvCarica,
        my_SpdConsSorvCaricaDesc: item?.my_SpdConsSorvCaricaDesc,
        my_SpdConsSorvIdOrganoDiCons: item?.my_SpdConsSorvIdOrganoDiCons,
        my_SpdConsSorvIdOrgano: item?.my_SpdConsSorvIdOrgano,
        my_SpdConsSorvCognome: item?.my_SpdConsSorvCognome,
        my_SpdConsSorvNome: item?.my_SpdConsSorvNome,
        my_SpdConsSorvCodFisc: item?.my_SpdConsSorvCodFisc,
        my_SpdConsSorvEmail: item?.my_SpdConsSorvEmail,
        my_SpdConsSorvCodFiscOrig: item?.my_SpdConsSorvCodFiscOrig,
        my_SpdConsSorvVia: item?.my_SpdConsSorvVia,
        my_SpdConsSorvProvinciaCH: item?.my_SpdConsSorvProvinciaCH,
        my_SpdConsSorvComuneCH: item?.my_SpdConsSorvComuneCH,
        my_SpdConsSorvProvincia: item?.my_SpdConsSorvProvincia,
        my_SpdConsSorvComune: item?.my_SpdConsSorvComune,
        my_SpdConsSorvCap: item?.my_SpdConsSorvCap,
        my_SpdConsSorvIdComune: item?.my_SpdConsSorvIdComune,
        my_SpdConsSorvIdProvincia: item?.my_SpdConsSorvIdProvincia,
        my_SpdConsSorvIdIndirizzo: item?.my_SpdConsSorvIdIndirizzo,
        my_SpdConsSorvDataElezione: item?.my_SpdConsSorvDataElezione,
        my_SpdConsSorvCkSocio: item?.my_SpdConsSorvCkSocio ?? '0',
        my_SpdConsSorvCkRevisore: item?.my_SpdConsSorvCkRevisore ?? '0',
        my_SpdConsSorvCaricaOrig: item?.my_SpdConsSorvCaricaOrig,
        my_SpdConsSorvDataElezioneOrig: item?.my_SpdConsSorvDataElezioneOrig,
        my_group14: item?.my_group14,
    });
    updateDependentControl(group, 'my_SpdConsSorvComuneCH', 'my_SpdConsSorvComune');
    updateDependentControl(group, 'my_SpdConsSorvProvinciaCH', 'my_SpdConsSorvProvincia');
    return group;
};

export const buildConsiglioSorveglianzaFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const elemCons = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza;
    rebuildFormArray(fb, elemCons, form, NORME_ARRAY_PATHS.consiglioSorveglianza, buildElemConsSorveglianzaGroup);
};

export const buildElemConsGestioneGroup = (fb: FormBuilder, item?: MySpdElemConsiglioGestioneEntity): FormGroup => {
    const group = fb.group({
        my_SpdConsGestCarica: item?.my_SpdConsGestCarica,
        my_SpdConsGestCaricaDesc: item?.my_SpdConsGestCaricaDesc,
        my_SpdConsGestIdOrganoDiCons: item?.my_SpdConsGestIdOrganoDiCons,
        my_SpdConsGestIdOrgano: item?.my_SpdConsGestIdOrgano,
        my_SpdConsGestCognome: item?.my_SpdConsGestCognome,
        my_SpdConsGestNome: item?.my_SpdConsGestNome,
        my_SpdConsGestCodFisc: item?.my_SpdConsGestCodFisc,
        my_SpdConsGestEmail: item?.my_SpdConsGestEmail,
        my_SpdConsGestCodFiscOrig: item?.my_SpdConsGestCodFiscOrig,
        my_SpdConsGestVia: item?.my_SpdConsGestVia,
        my_SpdConsGestProvinciaCH: item?.my_SpdConsGestProvinciaCH,
        my_SpdConsGestComuneCH: item?.my_SpdConsGestComuneCH,
        my_SpdConsGestProvincia: item?.my_SpdConsGestProvincia,
        my_SpdConsGestComune: item?.my_SpdConsGestComune,
        my_SpdConsGestCap: item?.my_SpdConsGestCap,
        my_SpdConsGestIdComune: item?.my_SpdConsGestIdComune,
        my_SpdConsGestIdProvincia: item?.my_SpdConsGestIdProvincia,
        my_SpdConsGestIdIndirizzo: item?.my_SpdConsGestIdIndirizzo,
        my_SpdConsGestDataElezione: item?.my_SpdConsGestDataElezione,
        my_SpdConsGestCkSocio: item?.my_SpdConsGestCkSocio ?? '0',
        my_SpdConsGestCkRevisore: item?.my_SpdConsGestCkRevisore ?? '0',
        my_SpdConsGestCaricaOrig: item?.my_SpdConsGestCaricaOrig,
        my_SpdConsGestDataElezioneOrig: item?.my_SpdConsGestDataElezioneOrig,
        my_group15: item?.my_group15,
    });
    updateDependentControl(group, 'my_SpdConsGestComuneCH', 'my_SpdConsGestComune');
    updateDependentControl(group, 'my_SpdConsGestProvinciaCH', 'my_SpdConsGestProvincia');
    return group;
};

export const buildConsiglioGestioneFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const elemCons = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaDualistico.my_SpdConsiglioGestione.my_SpdElemConsiglioGestione;
    rebuildFormArray(fb, elemCons, form, NORME_ARRAY_PATHS.consiglioGestione, buildElemConsGestioneGroup);
};

export const buildComitatoControlloGestioneFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const comitatoContr = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaMonistico.my_SpmComitatoGestione.my_SpmElemComitatoGestione;
    rebuildFormArray(fb, comitatoContr, form, NORME_ARRAY_PATHS.comitatoGestione, buildElemComitatoGestioneGroup);
};

export const buildElemComitatoGestioneGroup = (fb: FormBuilder, item?: MySpmElemComitatoGestioneEntity): FormGroup => {
    const group = fb.group({
        my_SpmComitGestCarica: item?.my_SpmComitGestCarica,
        my_SpmComitGestCaricaDesc: item?.my_SpmComitGestCaricaDesc,
        my_SpmComitGestIdOrganoDiCons: item?.my_SpmComitGestIdOrganoDiCons,
        my_SpmComitGestIdOrgano: item?.my_SpmComitGestIdOrgano,
        my_SpmComitGestCognome: item?.my_SpmComitGestCognome,
        my_SpmComitGestNome: item?.my_SpmComitGestNome,
        my_SpmComitGestCaricaOrig: item?.my_SpmComitGestCaricaOrig,
        my_SpmComitGestCodFisc: item?.my_SpmComitGestCodFisc,
        my_SpmComitGestEmail: item?.my_SpmComitGestEmail,
        my_SpmComitGestCodFiscOrig: item?.my_SpmComitGestCodFiscOrig,
        my_SpmComitGestVia: item?.my_SpmComitGestVia,
        my_SpmComitGestProvinciaCH: item?.my_SpmComitGestProvinciaCH,
        my_SpmComitGestComuneCH: item?.my_SpmComitGestComuneCH,
        my_SpmComitGestProvincia: item?.my_SpmComitGestProvincia,
        my_SpmComitGestComune: item?.my_SpmComitGestComune,
        my_SpmComitGestCap: item?.my_SpmComitGestCap,
        my_SpmComitGestIdComune: item?.my_SpmComitGestIdComune,
        my_SpmComitGestIdProvincia: item?.my_SpmComitGestIdProvincia,
        my_SpmComitGestIdIndirizzo: item?.my_SpmComitGestIdIndirizzo,
        my_SpmComitGestDataElezione: item?.my_SpmComitGestDataElezione,
        my_SpmComitGestCkSocio: item?.my_SpmComitGestCkSocio,
        my_SpmComitGestCkRevisore: item?.my_SpmComitGestCkRevisore,
        my_group17: item?.my_group17,
        my_SpmComitGestDataElezioneOrig: item?.my_SpmComitGestDataElezioneOrig,
    });
    updateDependentControl(group, 'my_SpmComitGestComuneCH', 'my_SpmComitGestComune');
    updateDependentControl(group, 'my_SpmComitGestProvinciaCH', 'my_SpmComitGestProvincia');
    return group;
};
